import type { CSSProperties } from 'react';
import { LOGO_SVG } from './logo-data';
import { BRAND_MARK_PATH, BRAND_MARK_VIEWBOX } from './brand-mark';

const PROVIDER_NAMES: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  xai: 'xAI',
  deepseek: 'DeepSeek',
  moonshot: 'Moonshot AI',
  zai: 'Z.ai',
  byteplus: 'BytePlus',
  mock: 'Simulator',
};

export function providerName(code: string | null | undefined): string {
  if (!code) return '—';
  return PROVIDER_NAMES[code] ?? code;
}

const PROVIDER_LOGO: Record<string, string> = {
  anthropic: 'anthropic',
  openai: 'openai',
  google: 'google',
  xai: 'xai',
  deepseek: 'deepseek',
  moonshot: 'moonshot',
  zai: 'zai',
  byteplus: 'bytedance',
};

function modelLogoKey(value: string): string | null {
  const v = value.toLowerCase();
  if (v.startsWith('claude')) return 'claude';
  if (v.startsWith('gpt') || /^o\d/.test(v) || v.startsWith('chatgpt')) return 'openai';
  if (v.startsWith('gemini') || v.startsWith('gemma')) return 'gemini';
  if (v.startsWith('grok')) return 'grok';
  if (v.startsWith('deepseek')) return 'deepseek';
  if (v.startsWith('kimi')) return 'kimi';
  if (v.startsWith('glm')) return 'zhipu';
  if (v.startsWith('seed') || v.startsWith('dreamina')) return 'bytedance';
  if (v.startsWith('doubao')) return 'doubao';
  return null;
}

/** Model mark, provider fallback. */
export function ModelLogo({
  model,
  family,
  provider,
  size = 28,
  bare = false,
  style,
}: {
  model?: string | null;
  family?: string | null;
  provider?: string | null;
  size?: number;
  bare?: boolean;
  style?: CSSProperties;
}) {
  const key =
    (family && modelLogoKey(family)) ||
    (model && modelLogoKey(model)) ||
    (provider ? PROVIDER_LOGO[provider] : undefined) ||
    null;
  const svg = key ? LOGO_SVG[key] : undefined;
  const label = (model ?? provider ?? '?').replace(/^mock-?/, '');
  return (
    <span
      className={bare ? 'logo bare' : 'logo'}
      style={{ width: size, height: size, fontSize: size, ...style }}
      title={provider ? providerName(provider) : undefined}
      aria-hidden
    >
      {svg ? (
        <span style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <span className="logo-fallback">{provider === 'mock' ? 'SIM' : label.slice(0, 2).toUpperCase()}</span>
      )}
    </span>
  );
}

export function ProviderLogo({ provider, size = 22, bare }: { provider: string | null | undefined; size?: number; bare?: boolean }) {
  return <ModelLogo provider={provider ?? undefined} size={size} bare={bare} />;
}

export function ModelChip({
  model,
  name,
  provider,
  family,
  size = 26,
  showId = true,
}: {
  model: string;
  name?: string | null;
  provider?: string | null;
  family?: string | null;
  size?: number;
  showId?: boolean;
}) {
  return (
    <span className="model-chip">
      <ModelLogo model={model} family={family} provider={provider} size={size} />
      <span style={{ minWidth: 0, display: 'grid' }}>
        <span className="model-name">{name ?? model}</span>
        {showId && name && <span className="model-id">{model}</span>}
      </span>
    </span>
  );
}

export function AuroraBackdrop({ style }: { style?: CSSProperties }) {
  return <div className="aurora" style={style} aria-hidden />;
}

export const BRAND_NAME = 'Partner AI';

export function MarkGlyph() {
  return (
    <svg viewBox={BRAND_MARK_VIEWBOX} aria-hidden>
      <path d={BRAND_MARK_PATH} fill="currentColor" />
    </svg>
  );
}

export function BrandMark({ sub }: { sub?: string }) {
  return (
    <>
      <span className="brand-mark">
        <MarkGlyph />
      </span>
      <span>
        {BRAND_NAME}
        {sub && <small>{sub}</small>}
      </span>
    </>
  );
}
